// Analyse de sentiment — avis clients analysés par l'IA
import { useState } from "react";
import { MessageSquare, ThumbsUp, ThumbsDown, Minus } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { KPICard } from "@/components/admin/shared/KPICard";
import { useSentimentAnalysis } from "@/hooks/admin/useAI";
import type { SentimentPeriod, SentimentValue } from "@/lib/admin-api/ai";
import React from "react";

// ---------------------------------------------------------------------------
// Period selector
// ---------------------------------------------------------------------------

const PERIODS: readonly { value: SentimentPeriod; label: string }[] = [
  { value: "7d", label: "7 derniers jours" },
  { value: "30d", label: "30 derniers jours" },
  { value: "90d", label: "90 derniers jours" },
];

// ---------------------------------------------------------------------------
// Sentiment helpers
// ---------------------------------------------------------------------------

const SENTIMENT_BADGE: Record<SentimentValue, string> = {
  positive: "bg-green-100 text-green-700",
  neutral: "bg-stone-100 text-stone-600",
  negative: "bg-red-100 text-red-700",
};

const SENTIMENT_LABELS: Record<SentimentValue, string> = {
  positive: "Positif",
  neutral: "Neutre",
  negative: "Négatif",
};

const SENTIMENT_ICONS: Record<SentimentValue, React.ElementType> = {
  positive: ThumbsUp,
  neutral: Minus,
  negative: ThumbsDown,
};

// ---------------------------------------------------------------------------
// Score color
// ---------------------------------------------------------------------------

function scoreClass(score: number): string {
  if (score >= 7) return "text-green-700";
  if (score >= 4) return "text-amber-600";
  return "text-red-600";
}

// ---------------------------------------------------------------------------
// Distribution bar
// ---------------------------------------------------------------------------

interface DistributionBarProps {
  readonly positive: number;
  readonly neutral: number;
  readonly negative: number;
}

function DistributionBar({ positive, neutral, negative }: DistributionBarProps) {
  return (
    <div className="flex h-3 w-full overflow-hidden rounded-full">
      <div className="bg-green-400" style={{ width: `${positive}%` }} title={`Positif : ${positive}%`} />
      <div className="bg-stone-300" style={{ width: `${neutral}%` }} title={`Neutre : ${neutral}%`} />
      <div className="bg-red-400" style={{ width: `${negative}%` }} title={`Négatif : ${negative}%`} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function SentimentAnalysis() {
  const [period, setPeriod] = useState<SentimentPeriod>("30d");

  const { data, isLoading, isError } = useSentimentAnalysis(period);

  const summary = data?.summary;
  const topics = data?.topics ?? [];
  const trend = data?.trend ?? [];

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header + period selector */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-stone-900">Analyse de sentiment</h1>
            <p className="mt-1 text-sm text-stone-500">
              Avis clients analysés automatiquement par l'IA
            </p>
          </div>
          <div className="flex gap-1 rounded-lg border border-stone-200 bg-white p-1">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setPeriod(p.value)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  period === p.value
                    ? "bg-stone-900 text-white"
                    : "text-stone-600 hover:bg-stone-50"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {isError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Impossible de charger les données. Veuillez réessayer.
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KPICard
            label="Score moyen"
            value={isLoading ? "…" : `${summary?.avg_score?.toFixed(1) ?? "—"}/10`}
            icon={<MessageSquare className="h-4 w-4" />}
          />
          <KPICard
            label="Avis positifs"
            value={isLoading ? "…" : `${summary?.positive_percent ?? 0}%`}
            icon={<ThumbsUp className="h-4 w-4" />}
          />
          <KPICard
            label="Avis neutres"
            value={isLoading ? "…" : `${summary?.neutral_percent ?? 0}%`}
            icon={<Minus className="h-4 w-4" />}
          />
          <KPICard
            label="Avis négatifs"
            value={isLoading ? "…" : `${summary?.negative_percent ?? 0}%`}
            icon={<ThumbsDown className="h-4 w-4" />}
          />
        </div>

        {/* Distribution bar */}
        {summary && !isLoading && (
          <section>
            <h2 className="mb-3 text-sm font-medium text-stone-700">
              Répartition des sentiments ({summary.total_reviews.toLocaleString("fr-SN")} avis)
            </h2>
            <DistributionBar
              positive={summary.positive_percent}
              neutral={summary.neutral_percent}
              negative={summary.negative_percent}
            />
            <div className="mt-2 flex gap-4 text-xs text-stone-500">
              <span className="flex items-center gap-1">
                <span className="h-2.5 w-2.5 rounded-sm bg-green-400 inline-block" />
                Positif {summary.positive_percent}%
              </span>
              <span className="flex items-center gap-1">
                <span className="h-2.5 w-2.5 rounded-sm bg-stone-300 inline-block" />
                Neutre {summary.neutral_percent}%
              </span>
              <span className="flex items-center gap-1">
                <span className="h-2.5 w-2.5 rounded-sm bg-red-400 inline-block" />
                Négatif {summary.negative_percent}%
              </span>
            </div>
          </section>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Topics table */}
          <section>
            <h2 className="mb-3 text-base font-semibold text-stone-800">Sujets principaux</h2>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="h-6 w-6 animate-spin rounded-full border-4 border-stone-200 border-t-stone-600" />
              </div>
            ) : topics.length === 0 ? (
              <p className="text-sm text-stone-400">Aucun sujet disponible.</p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-stone-200">
                <table className="w-full">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">
                        Sujet
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">
                        Mentions
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">
                        Sentiment
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100 bg-white">
                    {topics.map((topic) => {
                      const Icon = SENTIMENT_ICONS[topic.sentiment];
                      return (
                        <tr key={topic.topic} className="hover:bg-stone-50">
                          <td className="px-4 py-3 text-sm font-medium text-stone-900">
                            {topic.topic}
                          </td>
                          <td className="px-4 py-3 text-sm text-stone-700">{topic.mentions}</td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${SENTIMENT_BADGE[topic.sentiment]}`}
                            >
                              <Icon className="h-3 w-3" />
                              {SENTIMENT_LABELS[topic.sentiment]}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Trend table */}
          <section>
            <h2 className="mb-3 text-base font-semibold text-stone-800">Évolution du score</h2>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="h-6 w-6 animate-spin rounded-full border-4 border-stone-200 border-t-stone-600" />
              </div>
            ) : trend.length === 0 ? (
              <p className="text-sm text-stone-400">Aucune donnée de tendance disponible.</p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-stone-200">
                <table className="w-full">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">
                        Score (/10)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100 bg-white">
                    {trend.map((point) => (
                      <tr key={point.date} className="hover:bg-stone-50">
                        <td className="px-4 py-3 text-sm text-stone-700">
                          {new Date(point.date).toLocaleDateString("fr-SN")}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm font-semibold ${scoreClass(point.score)}`}>
                            {point.score.toFixed(1)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </AdminLayout>
  );
}
